import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, CheckCircle2, ShieldCheck, Phone, BadgeCheck, Users, Loader2 } from "lucide-react";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { toast } from "sonner";
import { CONTACT, fmtPhone, tel } from "../lib/contact";

const TYPES = ["Apartment", "Villa", "Residential Plot", "Commercial Property", "Rental", "Other"];
const AREA_TYPES = ["Residential Plot", "Villa"];
const TIMES = ["Morning (9am–12pm)", "Afternoon (12–4pm)", "Evening (4–8pm)", "Anytime"];
const DEFAULT_IMG = "https://images.pexels.com/photos/7937291/pexels-photo-7937291.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const AUTO_DELAY_MS = 45000;   // show auto popup after this long on the site
const AUTO_COOLDOWN_DAYS = 7;  // then stay quiet for a week

const BLANK = { inquiry_type: "Apartment", name: "", mobile: "", email: "", city: "", budget: "", min_area: "", message: "", preferred_time: "" };

export function LeadFormModal() {
  const { leadModal, closeLead, openLead } = useApp();
  const location = useLocation();
  const [form, setForm] = useState(BLANK);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const boxRef = useRef(null);
  const { open, property, type, title, auto } = leadModal;

  // Auto-open: after a delay, at most once per cooldown period, never on CRM/onboarding, never if already opened or submitted
  useEffect(() => {
    if (open) return;
    const p = window.location.pathname;
    if (p.startsWith("/crm") || p.startsWith("/onboard")) return;
    const last = Number(localStorage.getItem("pbs_popup_last") || 0);
    if (last && Date.now() - last < AUTO_COOLDOWN_DAYS * 864e5) return;
    const t = setTimeout(() => {
      if (sessionStorage.getItem("pbs_popup_session")) return;
      sessionStorage.setItem("pbs_popup_session", "1");
      localStorage.setItem("pbs_popup_last", String(Date.now()));
      openLead({ type: "enquiry", title: "Tell us what you're looking for", auto: true });
    }, AUTO_DELAY_MS);
    return () => clearTimeout(t);
  }, [open, openLead]);

  // Scroll lock + Escape + focus handling
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    setTimeout(() => boxRef.current?.querySelector("input, select, textarea, button")?.focus(), 50);
    const onKey = (e) => {
      if (e.key === "Escape") closeLead();
      if (e.key === "Tab" && boxRef.current) {
        const els = [...boxRef.current.querySelectorAll("a[href], button:not([disabled]), input, select, textarea")].filter((el) => el.offsetParent !== null);
        if (!els.length) return;
        const first = els[0], last = els[els.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prevOverflow; document.removeEventListener("keydown", onKey); prev?.focus?.(); };
  }, [open, closeLead]);

  const reset = useCallback(() => { setDone(false); setForm(BLANK); setErr(""); closeLead(); }, [closeLead]);
  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const heading = type === "visit" ? "Schedule a Site Visit" : type === "callback" ? "Request a Callback" : type === "brochure" ? "Get the Brochure" : title || "Real Estate Enquiry";

  const submit = async (e) => {
    e.preventDefault();
    const digits = form.mobile.replace(/\D/g, "").replace(/^91/, "");
    if (!form.name.trim()) return setErr("Please enter your full name.");
    if (!/^[6-9]\d{9}$/.test(digits)) return setErr("Enter a valid 10-digit Indian mobile number.");
    setErr(""); setSubmitting(true);
    const notes = [form.message, AREA_TYPES.includes(form.inquiry_type) && form.min_area ? `Min area: ${form.min_area}` : "", `Page: ${window.location.href}`].filter(Boolean).join("\n");
    try {
      await api.createLead({
        name: form.name.trim(), mobile: digits, email: form.email, city: form.city,
        property_interest: form.inquiry_type, budget: form.budget, preferred_time: form.preferred_time,
        source: auto ? "Website Popup" : "Website", lead_type: type,
        property_id: property?.id || "", property_name: property?.name || "", message: notes || title || "",
      });
      localStorage.setItem("pbs_popup_last", String(Date.now()));
      setDone(true);
    } catch { setErr("Something went wrong while submitting. Please try again."); toast.error("Could not submit your enquiry. Please try again."); }
    finally { setSubmitting(false); }
  };

  const img = property?.image || DEFAULT_IMG;
  const F = ({ label, children }) => (
    <label className="block">
      <span className="block text-[12px] font-semibold text-pbs-dark/80 mb-1">{label}</span>
      {children}
    </label>
  );

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-3" role="dialog" aria-modal="true" aria-label={heading} data-testid="lead-modal">
      <div className="absolute inset-0 bg-pbs-dark/40" onClick={reset} aria-hidden="true" />
      <div ref={boxRef}
        className="relative bg-white w-[calc(100vw-24px)] max-w-[400px] sm:max-w-[820px] rounded-2xl border border-pbs-line shadow-2xl overflow-hidden animate-fade-up max-h-[calc(100vh-24px)] max-h-[calc(100dvh-24px)] sm:max-h-[92vh] flex flex-col">
        {/* header (mobile-first: title + close on top) */}
        <div className="flex items-center justify-between gap-3 px-5 sm:px-7 pt-4 sm:pt-5 pb-1 shrink-0">
          <div className="min-w-0">
            <h3 className="text-lg sm:text-xl font-bold text-pbs-dark truncate" data-testid="lead-modal-title">{heading}</h3>
            {property && <p className="text-[12px] text-pbs-grey truncate">{property.name}{property.locality ? ` · ${property.locality}` : ""}</p>}
          </div>
          <button onClick={reset} aria-label="Close enquiry form" data-testid="lead-modal-close"
            className="w-9 h-9 shrink-0 rounded-full border border-[#E5EAE6] bg-white text-pbs-dark hover:text-pbs-purple hover:border-pbs-purple/40 flex items-center justify-center transition-colors">
            <X size={18} />
          </button>
        </div>

        {done ? (
          <div className="p-8 text-center overflow-y-auto">
            <span className="mx-auto w-14 h-14 rounded-full bg-pbs-purple/10 text-pbs-purple flex items-center justify-center"><CheckCircle2 size={30} /></span>
            <h4 className="mt-4 text-xl font-bold text-pbs-dark">Thank you, {form.name.split(" ")[0] || "there"}!</h4>
            <p className="mt-2 text-sm text-pbs-grey max-w-sm mx-auto">Your enquiry has been received{property ? ` for ${property.name}` : ""}. A PlotBySquare expert will contact you shortly.</p>
            <button onClick={reset} data-testid="lead-success-close" className="mt-6 px-8 py-3 rounded-lg bg-pbs-purple hover:bg-pbs-plum text-white font-bold transition-colors">Done</button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] min-h-0 flex-1 overflow-y-auto">
            {/* form column */}
            <form onSubmit={submit} className="p-5 sm:p-7 space-y-3.5 order-2 sm:order-1" data-testid="lead-form">
              {/* mobile compact image */}
              <div className="sm:hidden relative h-32 rounded-xl overflow-hidden mb-1 -mt-1">
                <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <F label="Inquiry type"><select data-testid="lead-type" value={form.inquiry_type} onChange={set("inquiry_type")} className="pbs-in">{TYPES.map((t) => <option key={t}>{t}</option>)}</select></F>
                <F label="Full name *"><input data-testid="lead-name" autoComplete="name" value={form.name} onChange={set("name")} className="pbs-in" placeholder="Your name" /></F>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <F label="Phone number *">
                  <div className="flex items-center pbs-in !p-0 overflow-hidden">
                    <span className="pl-3 pr-2 text-sm font-semibold text-pbs-grey shrink-0">+91</span>
                    <input data-testid="lead-mobile" inputMode="tel" autoComplete="tel-national" value={form.mobile} onChange={set("mobile")} className="flex-1 py-2.5 pr-3 outline-none text-sm min-w-0" placeholder="98XXX XXXXX" />
                  </div>
                </F>
                <F label="Email (optional)"><input data-testid="lead-email" type="email" autoComplete="email" value={form.email} onChange={set("email")} className="pbs-in" placeholder="you@email.com" /></F>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <F label="Preferred city / locality"><input data-testid="lead-city" value={form.city} onChange={set("city")} className="pbs-in" placeholder="e.g. Noida" /></F>
                <F label="Max budget"><input data-testid="lead-budget" value={form.budget} onChange={set("budget")} className="pbs-in" placeholder="e.g. ₹1.5 Cr" /></F>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {AREA_TYPES.includes(form.inquiry_type) && <F label="Min area (sq.ft)"><input data-testid="lead-area" inputMode="numeric" value={form.min_area} onChange={set("min_area")} className="pbs-in" placeholder="e.g. 175" /></F>}
                <F label="Preferred call time"><select data-testid="lead-time" value={form.preferred_time} onChange={set("preferred_time")} className="pbs-in"><option value="">Anytime</option>{TIMES.map((t) => <option key={t}>{t}</option>)}</select></F>
              </div>
              <F label="Message (optional)"><textarea data-testid="lead-message" value={form.message} onChange={set("message")} rows={2} className="pbs-in resize-none" placeholder="Any specific requirements?" /></F>
              {err && <p role="alert" data-testid="lead-error" className="text-[13px] font-semibold text-[#B91C1C]">{err}</p>}
              <button type="submit" disabled={submitting} data-testid="lead-submit"
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-lg bg-pbs-purple hover:bg-pbs-plum text-white font-bold transition-colors disabled:opacity-60">
                {submitting ? <Loader2 size={17} className="animate-spin" /> : <Phone size={16} />} {submitting ? "Submitting…" : "Submit Enquiry"}
              </button>
              <p className="text-[12px] text-pbs-grey text-center">Prefer to talk? Call <a href={tel(CONTACT.phone)} data-testid="lead-call-main" className="font-semibold text-pbs-purple hover:underline">{fmtPhone(CONTACT.phone)}</a></p>
              <p className="text-[11px] text-pbs-grey text-center leading-relaxed">
                <ShieldCheck size={12} className="inline -mt-0.5 mr-1 text-pbs-purple" />
                We only use your details to respond to this enquiry. See our <Link to="/privacy" onClick={reset} className="font-semibold text-pbs-purple hover:underline">Privacy Policy</Link>.
              </p>
            </form>

            {/* desktop image column */}
            <div className="hidden sm:flex flex-col order-2 relative min-h-0">
              <img src={img} alt={property ? `${property.name} photo` : "Premium homes by PlotBySquare"} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 bg-white/95 backdrop-blur border-t border-pbs-line p-4">
                <p className="text-[13px] font-semibold text-pbs-dark">Talk to a verified property expert</p>
                <div className="mt-2 flex items-center gap-4 text-[12px] text-pbs-grey">
                  <span className="flex items-center gap-1.5"><BadgeCheck size={15} className="text-pbs-purple" /> RERA-checked listings</span>
                  <span className="flex items-center gap-1.5"><Users size={15} className="text-pbs-purple" /> 200+ trusted partners</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <style>{`.pbs-in{width:100%;border:1px solid #E4EBE4;border-radius:10px;padding:10px 12px;font-size:14px;outline:none;background:#fff;color:#25352C;transition:border-color .15s,box-shadow .15s}.pbs-in:focus{border-color:#236B45;box-shadow:0 0 0 3px rgba(35,107,69,0.12)}`}</style>
    </div>
  );
}

export default LeadFormModal;
